import { useState, useMemo } from "react";
import { Calculator, AlertTriangle, Share2, RotateCcw, FileText } from "lucide-react";
import SEO from "../ui/SEO.jsx";
import {
  Card,
  Label,
  Helper,
  YenInput,
  SegmentedControl,
  RangeWithValue,
  NextButton,
  SecondaryButton,
  BackLink,
  SectionTitle,
  BreakdownRow,
  ProgressDots,
  Eyebrow,
  Disclaimer,
} from "../ui/primitives.jsx";
import {
  calculateDattaiKosei,
  calculateDattaiKokumin,
} from "../lib/tax.js";
import { useFxRates } from "../lib/useFxRates.jsx";
import { yen, brl } from "../lib/format.js";
import AdSlot from "../ui/AdSlot.jsx";
import { AD_SLOTS } from "../lib/adsense.js";

export default function DattaiCalc() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    pensionType: "kosei", // 'kosei' (employees) | 'kokumin' (national)
    monthsContributed: 36,
    averageMonthlySalary: 280000,
  });

  const result = useMemo(() => {
    if (form.pensionType === "kosei") {
      return {
        type: "kosei",
        ...calculateDattaiKosei({
          averageMonthlySalary: form.averageMonthlySalary,
          monthsContributed: form.monthsContributed,
        }),
      };
    }
    return {
      type: "kokumin",
      ...calculateDattaiKokumin({ monthsContributed: form.monthsContributed }),
    };
  }, [form]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const totalSteps = form.pensionType === "kosei" ? 2 : 1;

  return (
    <div>
      <SEO
        title="Calculadora de Dattai Ichijikin"
        description="Estimativa do reembolso da aposentadoria japonesa (dattai ichijikin) ao voltar para o Brasil. Inclui o desconto de 20,42% e recuperação via representante fiscal."
        path="/calculadoras/aposentadoria"
      />

      {step === 0 && (
        <StepPensionType form={form} update={update} onNext={() => setStep(1)} />
      )}
      {step === 1 && (
        <StepDetails
          form={form}
          update={update}
          onNext={() => setStep(form.pensionType === "kosei" ? 2 : 3)}
          onBack={() => setStep(0)}
        />
      )}
      {step === 2 && form.pensionType === "kosei" && (
        <StepSalary
          form={form}
          update={update}
          onNext={() => setStep(3)}
          onBack={() => setStep(1)}
        />
      )}
      {step === 3 && <ResultScreen form={form} result={result} onRestart={() => setStep(0)} />}

      {step < 3 && <ProgressDots current={step} total={form.pensionType === "kosei" ? 3 : 2} />}
    </div>
  );
}

function StepPensionType({ form, update, onNext }) {
  return (
    <div>
      <Eyebrow>Calculadora de aposentadoria</Eyebrow>
      <h1 className="font-serif font-medium text-[40px] md:text-[56px] leading-[1.05] tracking-tight mb-5">
        Quanto você pega de{" "}
        <span className="italic text-accent">aposentadoria</span>{" "}
        se voltar agora?
      </h1>
      <p className="text-base md:text-[17px] leading-relaxed text-ink-soft max-w-xl mb-10">
        Se você está pensando em voltar definitivamente, pode solicitar o
        dattai ichijikin (脱退一時金) — o reembolso parcial da sua contribuição
        para a aposentadoria. Tem regras específicas e uma armadilha de imposto.
      </p>

      <Card>
        <Label num="01">Qual aposentadoria você contribuiu?</Label>
        <SegmentedControl
          value={form.pensionType}
          onChange={(v) => update("pensionType", v)}
          options={[
            { value: "kosei", label: "Kosei Nenkin", sub: "厚生年金 · empresa" },
            { value: "kokumin", label: "Kokumin Nenkin", sub: "国民年金 · autônomo" },
          ]}
        />
        <Helper>
          Kosei = funcionário CLT japonês com desconto direto do salário.
          Kokumin = autônomo, freelancer, ou quem paga sozinho.
        </Helper>
      </Card>

      <NextButton onClick={onNext}>Próximo</NextButton>
    </div>
  );
}

function StepDetails({ form, update, onNext, onBack }) {
  return (
    <div>
      <BackLink onClick={onBack} />
      <h2 className="font-serif font-medium text-4xl leading-tight tracking-tight mb-8">
        Por quanto tempo você contribuiu?
      </h2>

      <Card>
        <Label num="02">Meses de contribuição</Label>
        <RangeWithValue
          min={0}
          max={120}
          value={form.monthsContributed}
          onChange={(v) => update("monthsContributed", v)}
          format={(v) => {
            const years = Math.floor(v / 12);
            const months = v % 12;
            if (v === 0) return "0 meses";
            if (years === 0) return `${months} ${months === 1 ? "mês" : "meses"}`;
            if (months === 0) return `${years} ${years === 1 ? "ano" : "anos"}`;
            return `${years} ano${years > 1 ? "s" : ""} e ${months} ${months === 1 ? "mês" : "meses"}`;
          }}
        />
        <Helper>
          Mínimo 6 meses para ter direito. Limite atual de cálculo: 60 meses
          (5 anos) — meses além disso não aumentam o reembolso, mas ainda são
          apagados do seu histórico.
        </Helper>

        {form.monthsContributed > 60 && (
          <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 px-4 py-3 rounded-r-lg">
            <div className="flex gap-2 items-start text-[13px] text-ink leading-relaxed">
              <AlertTriangle size={16} className="shrink-0 mt-0.5 text-yellow-700" />
              <div>
                Você passou do limite. Os {form.monthsContributed - 60} meses
                extras serão perdidos. Se você tem 10+ anos de contribuição,
                considere <strong>não</strong> pegar o lump-sum — você teria
                direito à aposentadoria proporcional aos 65.
              </div>
            </div>
          </div>
        )}

        {form.monthsContributed < 6 && form.monthsContributed > 0 && (
          <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 px-4 py-3 rounded-r-lg">
            <div className="flex gap-2 items-start text-[13px] text-ink leading-relaxed">
              <AlertTriangle size={16} className="shrink-0 mt-0.5 text-yellow-700" />
              <div>
                Mínimo de 6 meses não atingido — você ainda não tem direito
                ao reembolso.
              </div>
            </div>
          </div>
        )}
      </Card>

      <NextButton onClick={onNext}>Próximo</NextButton>
    </div>
  );
}

function StepSalary({ form, update, onNext, onBack }) {
  return (
    <div>
      <BackLink onClick={onBack} />
      <h2 className="font-serif font-medium text-4xl leading-tight tracking-tight mb-8">
        Qual foi seu salário médio mensal?
      </h2>

      <Card>
        <Label num="03">Salário médio (incluindo bônus)</Label>
        <YenInput
          value={form.averageMonthlySalary}
          onChange={(v) => update("averageMonthlySalary", v)}
        />
        <Helper>
          Média do <em className="font-serif italic">標準報酬月額</em> (salário
          mensal padrão) e bônus durante o período coberto. Se não souber
          exato, use uma média aproximada do seu salário bruto dos últimos anos.
        </Helper>
      </Card>

      <NextButton onClick={onNext}>Ver resultado</NextButton>
    </div>
  );
}

function ResultScreen({ form, result, onRestart }) {
  const { rates } = useFxRates();
  // Not eligible
  if (!result.eligible) {
    return (
      <div>
        <Eyebrow>Resultado</Eyebrow>
        <h2 className="font-serif font-medium text-4xl leading-tight tracking-tight mb-6">
          Ainda não dá pra solicitar.
        </h2>
        <Card className="mb-6">
          <p className="text-[17px] leading-relaxed text-ink">
            Você precisa ter contribuído por pelo menos <strong>6 meses</strong> para
            ter direito ao dattai ichijikin. Continue trabalhando até completar
            esse mínimo, ou se você está aqui pouco tempo e vai voltar logo,
            saiba que não conseguirá recuperar essas contribuições.
          </p>
        </Card>
        <SecondaryButton onClick={onRestart}>
          <RotateCcw size={16} />
          Refazer
        </SecondaryButton>
      </div>
    );
  }

  // Kokumin Nenkin result
  if (result.type === "kokumin") {
    const shareText =
      `Aqui no Japão — minha aposentadoria:\n\n` +
      `💴 Reembolso (kokumin nenkin): ${yen(result.amount)}\n` +
      `≈ ${brl(result.amount * rates.JPY_TO_BRL)}\n\n` +
      `Faça o seu: aquinojapao.com/calculadoras/aposentadoria`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

    return (
      <div>
        <Eyebrow>Seu resultado</Eyebrow>
        <h2 className="font-serif font-medium text-4xl leading-tight tracking-tight mb-8">
          Quanto você recupera.
        </h2>

        <div className="bg-ink text-bg rounded-2xl p-8 mb-4 relative overflow-hidden">
          <div className="text-xs tracking-[0.18em] uppercase opacity-60 mb-2">
            Reembolso aproximado
          </div>
          <div className="font-serif text-[56px] md:text-[72px] leading-none tracking-tight font-medium">
            {yen(result.amount)}
          </div>
          <div className="mt-3 text-sm opacity-70 font-mono">
            ≈ {brl(result.amount * rates.JPY_TO_BRL)}
          </div>
        </div>

        <Card variant="accent" className="mb-6">
          <SectionTitle icon={<FileText size={16} />}>
            Importante saber
          </SectionTitle>
          <ul className="space-y-2 text-[15px] text-ink leading-relaxed list-disc pl-5">
            <li>
              Reembolso de kokumin nenkin é <strong>livre de imposto</strong> (não tem o desconto de 20,42%).
            </li>
            <li>
              Você tem <strong>2 anos</strong> a contar da saída do Japão para solicitar.
            </li>
            <li>
              Todos os meses contribuídos são apagados do seu histórico — você
              não poderá usar esses meses para se aposentar aqui no futuro.
            </li>
            {result.monthsLost > 0 && (
              <li>
                Você tem <strong>{result.monthsLost} meses além do limite</strong> — eles serão perdidos no cálculo.
              </li>
            )}
          </ul>
        </Card>

        <ActionButtons whatsappUrl={whatsappUrl} onRestart={onRestart} />

        <Disclaimer>
          <strong className="text-ink">Aviso:</strong> valores estimativos com base
          na tabela oficial 2025 do Japan Pension Service. Para o valor exato,
          solicite na agência do nenkin antes de sair do país.
        </Disclaimer>

        <AdSlot slot={AD_SLOTS.calcResult} format="auto" />
      </div>
    );
  }

  // Kosei Nenkin result
  const shareText =
    `Aqui no Japão — minha aposentadoria:\n\n` +
    `💴 Reembolso bruto: ${yen(result.gross)}\n` +
    `📉 Desconto 20,42%: ${yen(result.withheld)}\n` +
    `✅ Você recebe inicialmente: ${yen(result.net)}\n` +
    `↩️ Recuperável (via rep. fiscal): ${yen(result.recoverable)}\n\n` +
    `Faça o seu: aquinojapao.com/calculadoras/aposentadoria`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  return (
    <div>
      <Eyebrow>Seu resultado</Eyebrow>
      <h2 className="font-serif font-medium text-4xl leading-tight tracking-tight mb-8">
        Quanto você recupera.
      </h2>

      <div className="bg-ink text-bg rounded-2xl p-8 mb-4 relative overflow-hidden">
        <div className="text-xs tracking-[0.18em] uppercase opacity-60 mb-2">
          Reembolso bruto
        </div>
        <div className="font-serif text-[56px] md:text-[72px] leading-none tracking-tight font-medium">
          {yen(result.gross)}
        </div>
        <div className="mt-3 text-sm opacity-70 font-mono">
          {result.monthsCounted} meses × fator {result.factor.toFixed(1)}
        </div>
      </div>

      <Card className="mb-4">
        <SectionTitle icon={<Calculator size={16} />}>
          O que realmente chega
        </SectionTitle>
        <BreakdownRow label="Valor bruto" value={yen(result.gross)} />
        <BreakdownRow
          label="Imposto retido (20,42%)"
          value={`-${yen(result.withheld)}`}
        />
        <BreakdownRow
          label="Você recebe inicialmente"
          value={yen(result.net)}
          bold
        />
      </Card>

      <Card variant="accent" className="mb-4">
        <SectionTitle icon={<FileText size={16} />}>
          Recuperação do imposto
        </SectionTitle>
        <div className="font-serif text-3xl font-medium text-ink mb-2">
          + {yen(result.recoverable)} <span className="text-base text-ink-soft">recuperável</span>
        </div>
        <p className="text-[15px] text-ink-soft leading-relaxed">
          Os 20,42% retidos podem voltar via um <strong>representante fiscal</strong>{" "}
          (納税管理人 / nouzei kanrinin) que você precisa{" "}
          <strong>nomear antes de sair do Japão</strong>. Ele faz a declaração no
          ano seguinte e recebe o reembolso na conta dele para te repassar.
        </p>
      </Card>

      <Card className="mb-6">
        <SectionTitle icon={<AlertTriangle size={16} />}>
          Pontos críticos
        </SectionTitle>
        <ul className="space-y-2 text-[15px] text-ink leading-relaxed list-disc pl-5">
          <li>
            Prazo de solicitação: <strong>2 anos</strong> a contar da saída do Japão.
          </li>
          <li>
            Limite atual de cálculo: <strong>60 meses (5 anos)</strong>.
            {result.monthsLost > 0 && (
              <> Seus {result.monthsLost} meses extras não contam.</>
            )}
          </li>
          <li>
            Pegar o reembolso <strong>apaga todos os meses</strong> contribuídos
            do seu histórico — incluindo os que excederam o limite.
          </li>
          <li>
            Se você planeja morar 10+ anos no Japão (ou voltar e contribuir
            mais), <strong>não pegue</strong> — você teria direito à
            aposentadoria proporcional aos 65 anos.
          </li>
          <li>
            O Brasil tem acordo previdenciário com o Japão — meses
            contribuídos lá podem ser somados para qualificar aposentadoria
            brasileira. Avaliar antes de pegar o lump-sum.
          </li>
        </ul>
      </Card>

      <ActionButtons whatsappUrl={whatsappUrl} onRestart={onRestart} />

      <Disclaimer>
        <strong className="text-ink">Aviso:</strong> esta é uma estimativa
        baseada na fórmula oficial do Japan Pension Service (fator{" "}
        {result.factor.toFixed(1)} × salário médio). O valor real depende do
        seu salário padrão registrado (標準報酬月額) e bônus reais. Para o
        cálculo exato, peça extrato no nenkin office antes de sair do Japão.
      </Disclaimer>

      <AdSlot slot={AD_SLOTS.calcResult} format="auto" />
    </div>
  );
}

function ActionButtons({ whatsappUrl, onRestart }) {
  return (
    <>
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2.5 bg-accent text-white py-4 px-6 rounded-xl font-semibold no-underline mb-3 hover:bg-accent/90 transition-colors"
      >
        <Share2 size={18} />
        Compartilhar no WhatsApp
      </a>
      <SecondaryButton onClick={onRestart}>
        <RotateCcw size={16} />
        Refazer o cálculo
      </SecondaryButton>
    </>
  );
}
